/** @jsxImportSource minimal-view */

// import { Token } from 'dsplang'
import { clamp, Point, Rect } from 'geometrik'
import { dep, Dep, effect, element, on, queue, render, view, web } from 'minimal-view'
import { screen } from '../screen'
import { skin } from '../skin'
import { observe } from '../util/observe'
import { luminate } from 'everyday-utils'
import { IS_MOBILE } from '../env'
import { SINGLE_CLICK_MS } from '../constants'
import { hmr } from '../hmr'

export interface Dim<T = any> {
  line: number
  start: number
  end: number
  bottom?: number
  height?: number
  meta?: T
}

export interface Snapshot {
  code: string
  line: number
  col: number
  coli: number
  selectionStart: Point
  selectionEnd: Point
}

export interface Key {
  key: string
  ctrlKey?: boolean
  metaKey?: boolean
  shiftKey?: boolean
  altKey?: boolean
}

export interface Deco extends Dim {
  height: number
  center: boolean
  preserveAspectRatio: boolean
  meta?: any
  canvas?: HTMLCanvasElement
  isVisible?: boolean
  update?: () => void
  draw?: (c: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, dim: Dim) => void
}

export interface Sub extends Dim {
  height: number
  meta?: any
  onPointerDown?: (e: PointerEvent) => void
  onPointerMove?: (e: PointerEvent) => void
  onPointerLeave?: () => void
  draw: (c: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, sub: Sub) => void
}

export interface Marker extends Dim {
  meta?: any
  onPointerDown?: (e: PointerEvent) => void
  onPointerMove?: (e: PointerEvent) => void
  onPointerLeave?: () => void
  draw: (c: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, marker: Marker) => void
}

export interface SyntaxToken { type: any, text: string, line: number, col: number, meta?: any }

export interface LineCol {
  line: number
  col: number
}

async function rest() {
  await Promise.resolve()
  await Promise.resolve()
}

// async function araf() {
//   await new Promise(resolve => requestAnimationFrame(resolve))
// }

function lineBegin(line: string) {
  return line.match(/[^\s]|$/m)!.index!
}

function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const Open = {
  '(': ')',
  '[': ']',
  '{': '}',
} as any

const Close = {
  ')': '(',
  ']': '[',
  '}': '{',
} as any

const openers = new Set(Object.keys(Open))
const closers = new Set(Object.keys(Close))

function findMatchingBrackets(s: string, i: number): [number, number] | undefined {
  let char
  const stack = []
  let max = 1000

  --i

  const L = s[i]
  const R = s[i + 1]
  const LO = Open[L]
  const RO = Open[R]
  const LC = Close[L]
  const RC = Close[R]

  if (LC && RO) i++
  else if ((LO || RO) && (LC || RC)) { }
  else if (RO && !LO) i++
  else if (LC && !RC) i--

  while (i >= 0) {
    if (!--max) return
    char = s[i--]
    if (closers.has(char)) {
      stack.push(Close[char])
    }
    else if (stack.at(-1) === char) {
      stack.pop()
    }
    else if (openers.has(char)) {
      stack.push(char)
      break
    }
  }
  const openIndex = ++i
  const open = stack.at(-1)
  while (i < s.length) {
    if (!--max) return
    char = s[i++]
    if (openers.has(char)) {
      stack.push(Open[char])
    }
    else if (stack.at(-1) === char) {
      stack.pop()
      if (stack.length === 1 && Close[char] === open) return [openIndex, i - 1]
    }
    else if (closers.has(char)) {
      return
    }
  }
}

const NONSPACE = /[^\s]/g
const SPACE = /\s/g
const WORD = /[\s]{2,}|[./\\()"'\-:,.;<>~!@#$%^&*|+=[\]{}`~?\b ]{1}|\w+/g
const TOKEN = /.+?\b|.\B|\b.+?/g
const parseWords = (regexp: RegExp, text: any) => {
  regexp.lastIndex = 0
  let word
  const words = []
  while ((word = regexp.exec(text))) words.push(word)
  return words
}

// DONE:
// *- history
// *- ctrl+delete/backspace
// *- auto indent
// *- comments (*ctrl /, *ctrl shift /)
// *- match brackets
// *- selection actions (*delete / *move lines)
// *- clipboard
// *- *double click select word, *triple select block
// *- ctrl+a
// *- tab
//
// DOING:
// - decorations
//
// TODO:
// - find
// - multi cursor
// - markers
// - lenses
// - handle scrollLeft

export type Editor = typeof Editor.State

export const Editor = hmr(web(view('editor',
  class props {
    name!: string
    value!: Dep<string>
    rect!: Rect
    lineComment?: string = ';'
    deco?: Deco[] = []
    sub?: Sub[] = []
    markers?: Marker[] = []
    tokens?: SyntaxToken[]
    onKeyDown?: (e: Key) => void
  },
  class local {
    host = element
    canvas?: HTMLCanvasElement
    c?: CanvasRenderingContext2D
    textCanvas: HTMLCanvasElement = document.createElement('canvas')
    tc: CanvasRenderingContext2D = this.textCanvas.getContext('2d')!

    fontsReady?: boolean
    // importStyle = Object.assign(document.createElement('style'), {
    //   textContent: `@import url('https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed&family=Baumans&family=Geo&family=JetBrains+Mono:ital,wght@0,100;0,200;0,300;0,400;0,500;1,300&family=Jost:ital@0;1&display=swap');`
    // })

    code?: string
    fontSize = 12
    lineHeight = 0
    lineTops: number[] = [0]
    lineBottoms: number[] = [0]
    lineLongest: number = 0
    innerHeight = 0
    innerWidth = 0
    scrollTop = 0
    scrollLeft = 0
    lines?: string[]
    line = 0
    col = 0 // actual col
    coli = 0 // col intent
    charWidth = 0
    hasSelection = false
    selectionStart: Point = new Point()
    selectionEnd: Point = new Point()
    selectionText = ''
    history: Snapshot[] = []
    historyIndex = 0
    hiddenTextArea?: HTMLTextAreaElement
    shiftKey = false
    brackets?: [LineCol, LineCol] | false
    hasFocus = false
    decoMap?: Map<number, Deco[]>
    decoHeights?: Map<number, number>
    subMap?: Map<number, Sub[]>
    subHeights?: Map<number, number>
    cursor = 'text'
    hoveringPos?: LineCol = { line: 0, col: 0 }
    isHovering = false
    isTyping = false
    hoveringMarker?: Marker | false = false
    hoveringSub?: Sub | false = false
    elevations: { start: Point, end: Point, depth: number }[] = []
    elevated: LineCol[] = []
    elevationPoint?: LineCol | false = false
    cursors: Set<Point & { color?: string }> = new Set()
    isPanning = false
    scrollbarY = new Rect()
    scrollbarX = new Rect()
    hoveringScrollbarY = false
    hoveringScrollbarX = false
  },
  function actions({ $, fn, fns }) {
    const p = new Point()
    const r = new Rect(0, 0, 50, 50)
    const op = 'red'
    const brace = 'yellow'

    const specialKeys: any = {
      'Enter': '\n',
      'Tab': '  ',
      'Space': ' ',
    }

    let downTime = 0
    let downCount = 0

    const colors: Record<string, string> = {
      id: 'brightRed',
      number: 'white',
      string: 'brightYellow',
      comment: 'brightBlack',
      blockcomment: 'brightBlack',
      '[': brace,
      ']': brace,
      '{': brace,
      '}': brace,
      '(': brace,
      ')': brace,
      '+': op,
      '-': op,
      '*': op,
      '^': op,
      '%': op,
      '!': op,
      '/': op,
      'L': 'brightCyan',
      'R': 'brightCyan',
      'LR': 'brightCyan',
      '=': op,
      '+=': op,
      '\\': 'yellow',
      '@': 'brightPurple',
      '@~': 'brightPurple',
      ':': 'red',
      '.': 'red',
      ',': 'brightPurple',
      't': 'brightPurple',
      'M': 'brightRed',
      'S': 'brightCyan',
      'pick': 'brightPurple',
      'floor': 'brightPurple',
      'slide': 'brightPurple',
      'to_audio': 'brightPurple',
    }

    const saveHistory = () => {
      const snapshot = saveHistoryMeta()
      if (!snapshot) return
      snapshot.code = $.code!
      if ($.historyIndex < $.history.length - 1) {
        $.history = $.history.slice(0, $.historyIndex + 1)
      }
      $.historyIndex = $.history.push(snapshot as Snapshot) - 1
    }

    const saveHistoryMeta = () => {
      const snapshot: Partial<Snapshot> = {
        line: $.line,
        col: $.col,
        coli: $.coli,
        selectionStart: $.selectionStart.clone(),
        selectionEnd: $.selectionEnd.clone(),
      }
      const current = $.history[$.historyIndex]
      if ($.code === current?.code) {
        Object.assign(current, snapshot)
        return
      }
      return snapshot
    }

    const historic = <T extends (...args: any[]) => any>(fn: T): T & { sansHistory: T } => {
      return Object.assign(function (this: any, ...args: any[]) {
        saveHistory()
        const result = fn.apply(this, args)
        saveHistory()
        return result
      } as T, { sansHistory: fn })
    }

    return fns(new class actions {
      saveHistory = saveHistory
      saveHistoryMeta = saveHistoryMeta
      saveHistoryDebounced = queue.debounce(300).first.last(saveHistory)

      undo = historic(() => {
        if ($.historyIndex > 0) {
          Object.assign($, $.history[--$.historyIndex])
        }
      })

      redo = historic(() => {
        if ($.historyIndex < $.history.length - 1) {
          Object.assign($, $.history[++$.historyIndex])
        }
      })

      deleteSelection = historic(() => {
        if ($.selectionStart.equals($.selectionEnd)) return
        const { top, bottom } = this.getSelectionSorted()
        const a = this.getIndexFromCoords(top)
        const b = this.getIndexFromCoords(bottom)
        const removing = $.code!.slice(a, b)
        const charRight = $.code![b]
        $.code = $.code!.slice(0, a) + $.code!.slice(b)
        $.line = top.y
        $.coli = $.col = top.x
        $.selectionStart = $.selectionEnd = new Point($.coli, $.line)

        rest().then(() => {
          if (closers.has(charRight) && removing === Close[charRight]) {
            this.handleKey({ key: 'Delete' })
          }
        })
      })

      deleteLine = async (y: number) => {
      }

      shiftDecoTokenLines = (y: number, amt: number) => {
        // if ($.deco) {
        //   $.deco.forEach((d) => {
        //     if (d.line >= y) {
        //       d.line += amt
        //     }
        //     return d
        //   })
        //   $.decoHeights = this.getDecoHeights()
        //   // this.updateLineTops()
        // }
        // $.tokens!.forEach((t) => {
        //   if (t.line >= y) t.line += amt
        // })
      }

      getDecoHeights = () => {
        const decoMap = new Map<number, Deco[]>()
        const decoHeights = new Map<number, number>()

        for (const d of $.deco!) {
          if (decoMap.has(d.line)) {
            decoMap.get(d.line)!.push(d)
          }
          else {
            decoMap.set(d.line, [d])
          }

          decoHeights.set(
            d.line,
            Math.max(
              decoHeights.get(d.line) ?? 0,
              d.height
            )
          )
        }

        // $.decoMap = decoMap
        // $.decoHeights = decoHeights
        return decoHeights
      }

      updateLineTops = () => {
        const { lines, lineHeight, decoHeights, subHeights } = $

        const lineTops = Array.from<number>({ length: lines!.length + 1 })
        const lineBottoms = Array.from<number>({ length: lines!.length + 1 })

        for (let y = 0, top = 0; y <= lines!.length; y++) {
          lineTops[y] = top
          top += lineHeight + (decoHeights!.get(y) ?? 0) + (subHeights!.get(y) ?? 0)
          lineBottoms[y] = top
        }

        if (!$.lineTops || !$.lineBottoms
          || $.lineTops.length !== lineTops.length
          || $.lineBottoms.length !== lineBottoms.length
          || (
            lineTops.some((x, i) => x !== $.lineTops[i])
            || lineBottoms.some((x, i) => x !== $.lineBottoms[i])
          )) {
          $.lineTops = lineTops
          $.lineBottoms = lineBottoms
        }
      }

      handleBlur = () => {
        $.hasFocus = false
      }

      handleFocus = () => {
        $.hasFocus = true
      }

      handleKey = async (event: Key) => {
        const { hasSelection, onKeyDown } = $

        if (onKeyDown?.(event)) return

        const { key, ctrlKey = false, metaKey = false, shiftKey = false, altKey = false } = event

        let lines = $.lines!
        let text = lines[$.line]
        const currentChar = text[$.col]
        const maxLine = lines.length - 1
        const page = Math.floor($.rect.height / $.lineHeight)

        let x: number
        let dy: number
        let targetLine: number

        const char: string = key in specialKeys
          ? specialKeys[key]
          : key.length === 1 && key.match(/[\S\s]/)
            ? key
            : ''

        $.shiftKey = shiftKey

        switch (key) {
          case 'Control':

            return

          case 'Shift':

            if (!hasSelection) {
              $.selectionStart = $.selectionEnd = new Point($.col, $.line)
            }
            break

          case 'ArrowDown':

            if ($.line === maxLine) break

            // move line(s)
            if (altKey || (shiftKey && (ctrlKey || metaKey))) {
              this.saveHistoryDebounced()
              if (!hasSelection) {
                const line = lines.splice($.line, 1)[0]
                lines.splice(++$.line, 0, line)
                $.code = lines.join('\n')
                await rest()
                $.selectionStart = $.selectionEnd
                break
              }
              else {
                const { top, bottom } = this.getSelectionSorted()
                if (bottom.y === maxLine) break

                const slice = lines.splice(top.y, bottom.y - top.y + 1)
                lines.splice(++top.y, 0, ...slice)
                ++bottom.y
                $.code = lines.join('\n')
              }
            }

            ++$.line

            break

          case 'ArrowUp':

            if ($.line === 0) break

            // move line(s)
            if (altKey || (shiftKey && (ctrlKey || metaKey))) {
              this.saveHistoryDebounced()
              if (!hasSelection) {
                const line = lines.splice($.line, 1)[0]
                lines.splice(--$.line, 0, line)
                $.code = lines.join('\n')
                await rest()
                $.selectionStart = $.selectionEnd
                break
              }
              else {
                const { top, bottom } = this.getSelectionSorted()
                if (top.y === 0) break

                const slice = lines.splice(top.y, bottom.y - top.y + 1)
                lines.splice(--top.y, 0, ...slice)
                --bottom.y
                $.code = lines.join('\n')
              }
            }

            --$.line

            break

          case 'ArrowRight':

            if ($.col === text.length && $.line === maxLine) break
            if ($.col < text.length && (ctrlKey || metaKey)) {
              const words = parseWords(WORD, text)
              let word
              out: {
                for (let i = 0; i < words.length; i++) {
                  word = words[i]
                  if (word.index > $.col) {
                    $.col = word.index
                    break out
                  }
                }
                $.col = text.length
              }
            }
            else {
              ++$.col
            }
            if ($.col > text.length) {
              $.col = 0
              ++$.line
            }
            $.coli = $.col
            break

          case 'ArrowLeft':

            if ($.col === 0 && $.line === 0) break
            if ($.col > 0 && (ctrlKey || metaKey)) {
              const words = parseWords(WORD, text)
              let word
              out: {
                for (let i = words.length - 1; i >= 0; --i) {
                  word = words[i]
                  if (word.index < $.col) {
                    $.col = word.index
                    break out
                  }
                }
                $.col = 0
              }
            }
            else {
              --$.col
            }
            if ($.col < 0) {
              --$.line
              text = lines[$.line]
              $.col = text.length
            }
            $.coli = $.col
            break

          case 'PageDown':
          case 'PageUp':

            if (key === 'PageDown') {
              targetLine = $.line + page
              if (targetLine >= maxLine) {
                targetLine = maxLine
                $.coli = $.col = lines[maxLine].length
              }
            }
            else {
              targetLine = $.line - page
              if (targetLine <= 0) {
                targetLine = 0
                $.coli = $.col = 0
              }
            }
            dy = targetLine - $.line
            $.line = targetLine
            $.scrollTop -= dy * $.lineHeight
            break

          case 'Home':

            NONSPACE.lastIndex = 0
            x = NONSPACE.exec(text)?.index ?? 0
            $.coli = $.col = x === $.col ? 0 : x
            break

          case 'End':

            $.coli = $.col = text.length
            break

          case 'Backspace':
            this.saveHistoryDebounced()

            if ($.hasSelection) {
              this.deleteSelection.sansHistory()
              break
            }

            if ($.col > 0 && (ctrlKey || metaKey)) {
              this.saveHistoryMeta()
              $.selectionStart = new Point($.col, $.line)
              this.handleKey({ key: 'ArrowLeft', ctrlKey: true, shiftKey: true })
              await rest()
              this.handleKey({ key })
              break
            }

            if ($.col === 0) {
              if ($.line > 0) {
                const y = $.line

                // this.shiftDecoTokenLines(y, -1)
                // await rest()

                --$.line
                $.col = $.coli = lines[$.line].length
                lines[$.line] += text
                $.lines!.splice(y, 1)
                $.code = $.lines!.join('\n')
                // this.draw()

                // this.deleteLine($.line + 1)
                // lines.splice($.line + 1, 1)
                // $.code = lines.join('\n')
              }
            }
            else {
              lines[$.line] =
                text.slice(0, $.col - 1)
                + text.slice($.col)
              $.coli = $.col - 1
              $.col = $.coli
              $.code = lines.join('\n')
            }

            if (closers.has(currentChar) && text[$.col] === Close[currentChar]) {
              this.handleKey({ key: 'Delete' })
            }
            break

          case 'Delete':
            this.saveHistoryDebounced()

            if ($.hasSelection) {
              this.deleteSelection.sansHistory()
              break
            }

            if (shiftKey) {
              if ($.line === maxLine && !maxLine) {
                if (text.length) {
                  $.selectionStart = new Point(0, 0)
                  $.selectionEnd = new Point(text.length, 0)
                  this.deleteSelection()
                }
                break
              }

              $.selectionStart = new Point(0, $.line)
              if ($.line === maxLine && $.col === 0) {
                $.selectionEnd = new Point(0, $.line - 1)
              }
              else {
                $.selectionEnd = new Point(0, $.line + 1)
              }
              this.deleteSelection()
              break
            }

            if (ctrlKey || metaKey) {
              this.saveHistoryMeta()
              $.selectionStart = new Point($.col, $.line)
              this.handleKey({ key: 'ArrowRight', ctrlKey: true, shiftKey: true })
              await rest()
              this.handleKey({ key })
              break
            }

            if ($.col === text.length) {
              if ($.line < maxLine) {
                lines[$.line] =
                  text.slice(0, $.col)
                  + lines[$.line + 1]
                lines.splice($.line + 1, 1)
                $.code = lines.join('\n')
              }
            }
            else {
              lines[$.line] =
                text.slice(0, $.col)
                + text.slice($.col + 1)
              $.code = lines.join('\n')
            }

            break

          case 'Tab':
            if ($.hasSelection || shiftKey) {
              this.saveHistoryDebounced()

              const { hasSelection } = $
              let index: number = Infinity
              let lns: string[]
              let y: number
              if (hasSelection) {
                const { top, bottom } = this.getSelectionSorted()
                for (let i = top.y; i <= bottom.y; i++) {
                  index = Math.min(index, lineBegin(lines[i]))
                }
                y = top.y
                lns = lines.slice(top.y, bottom.y + 1)
              }
              else {
                index = lineBegin(text)
                y = $.line
                lns = [lines[y]]
              }

              const dec = shiftKey
              if (dec && !index) return

              let diff!: number
              const tabSize = 2
              const tab = ' '.repeat(tabSize)
              lns.forEach((text, i) => {
                if (dec) {
                  diff = -tabSize
                  text = text.replace(new RegExp(`^(\t| {1,${tabSize}})`, 'gm'), '')
                }
                else {
                  diff = +tabSize
                  text = text.length === 0 ? tab : text.replace(/^[^\n]/gm, `${tab}$&`)
                }
                lines[y + i] = text
              })
              $.coli = Math.max(0, $.coli + diff)
              $.col = $.coli

              $.code = lines.join('\n')
              if (hasSelection) {
                $.selectionStart.x += diff
                $.selectionEnd.x += diff
              }
              else {
                await rest()
                $.selectionStart = $.selectionEnd
              }
              return
            }

          // eslint-disable-next-line no-fallthrough
          default:

            if (ctrlKey || metaKey) {
              switch (key) {
                case 'a': {
                  const top = this.getLineColFromIndex(0)
                  const bottom = this.getLineColFromIndex($.code!.length)
                  $.selectionStart = new Point(top.col, top.line)
                  $.selectionEnd = new Point(bottom.col, bottom.line)
                  break
                }

                case 'D':
                  this.saveHistoryDebounced()
                  if (hasSelection) {
                    const { top, bottom } = this.getSelectionSorted()
                    const a = this.getIndexFromCoords(top)
                    const b = this.getIndexFromCoords(bottom)
                    $.code = $.code!.slice(0, b)
                      + $.code!.slice(a, b)
                      + $.code!.slice(b)
                    const lineCol = this.getLineColFromIndex(b + (b - a))
                    $.selectionStart = bottom
                    $.selectionEnd = new Point(lineCol.col, lineCol.line)
                    $.line = lineCol.line
                    $.coli = $.col = lineCol.col
                  }
                  else {
                    lines.splice($.line, 0, text)
                    $.code = lines.join('\n')
                    $.line++
                    await rest()
                    $.selectionStart = $.selectionEnd
                  }
                  break

                case 'z':
                  this.undo()
                  break

                case 'y':
                  this.redo()
                  break

                // toggle line comment

                case ':':
                case '?': {
                  this.saveHistoryDebounced()

                  if (hasSelection) {
                    const { top, bottom } = this.getSelectionSorted()
                    const a = this.getIndexFromCoords(top)
                    const b = this.getIndexFromCoords(bottom)
                    const dx = 2 + (top.y === bottom.y ? 2 : 0)
                    if ($.code!.slice(a, a + 2) === '/;'
                      && $.code!.slice(b - 2, b) === ';/'
                    ) {
                      $.code = $.code!.slice(0, a)
                        + $.code!.slice(a + 2, b - 2)
                        + $.code!.slice(b)
                      if ($.line === bottom.y && $.col === bottom.x) $.coli -= dx
                      bottom.x -= dx
                    }
                    else {
                      $.code = $.code!.slice(0, a)
                        + '/;'
                        + $.code!.slice(a, b)
                        + ';/'
                        + $.code!.slice(b)
                      if ($.line === bottom.y && $.col === bottom.x) $.coli += dx
                      bottom.x += dx
                    }
                    $.col = $.coli
                  }
                  else {
                    const index = this.getIndexFromCoords({ x: $.col, y: $.line })
                    const match = findMatchingBrackets($.code!, index)
                    if (match) {
                      if ($.code![match[0] + 1] === ';') {
                        $.code = $.code!.slice(0, match[0] + 1) + $.code!.slice(match[0] + 2)
                      }
                      else {
                        $.code = $.code!.slice(0, match[0] + 1) + ';' + $.code!.slice(match[0] + 1)
                      }
                    }
                    await rest()
                    $.selectionStart = $.selectionEnd
                  }
                  break
                }

                case ';':
                case '/': {
                  this.saveHistoryDebounced()

                  const c = $.lineComment!
                  const ce = escapeRegExp(c)
                  let index: number = Infinity
                  let lns: string[]
                  let y: number
                  if ($.hasSelection) {
                    const { top, bottom } = this.getSelectionSorted()
                    for (let i = top.y; i <= bottom.y; i++) {
                      index = Math.min(index, lineBegin(lines[i]))
                    }
                    y = top.y
                    lns = lines.slice(top.y, bottom.y + 1)
                  }
                  else {
                    index = lineBegin(text)
                    y = $.line
                    lns = [lines[y]]
                  }

                  let diff!: number
                  const dec = lns.every((text) => text.trimStart().slice(0, c.length) === c)
                  lns.forEach((text, i) => {
                    if (dec) {
                      const r = new RegExp(`^([^${ce}]*)${ce} ?`, 'gm')
                      diff = -(c.length + 1)
                      text = text.replace(r, '$1')
                    }
                    else {
                      const r = new RegExp(`^(?!$)([^${ce}]{0,${index}})`, 'gm')
                      diff = +(c.length + 1)
                      text = text.length === 0
                        ? c + ' '
                        : text.replace(r, `$1${c} `)
                    }
                    lines[y + i] = text
                  })
                  $.coli = Math.max(0, $.coli + diff)
                  $.col = $.coli
                  if ($.hasSelection) {
                    $.selectionStart.x += diff
                    $.selectionEnd.x += diff
                  }
                  $.code = lines.join('\n')
                  return
                }
              }
            }

            if (ctrlKey || metaKey || altKey) break

            if (char.length) {
              this.saveHistoryDebounced()

              if ($.hasSelection) {
                this.deleteSelection.sansHistory()
                // await Promise.resolve()
                lines = $.code!.split('\n')
                // await rest()
                // await araf()
                // lines = $.lines!
                text = lines[$.line]
              }

              const hasBracketLeft = Open[text[$.col - 1]]
              const hasBracketRight = Close[text[$.col]]
              const hasSpaceRight = ($.col === text.length) || SPACE.test(text[$.col])
              const isInBrackets = (($.col === text.trimEnd().length - 1) && hasBracketRight && hasBracketLeft)
              const beginOfLine = lineBegin(text) ?? 0

              const isEnter = char === '\n'
              const indent = (isEnter
                ? ' '.repeat($.col === text.trimEnd().length || isInBrackets ? beginOfLine : $.col)
                + ' '.repeat(hasBracketLeft ? 2 : 0)
                : '')

              const isCharSameAsBracketRight = closers.has(char) && char === text[$.col]

              if (!isCharSameAsBracketRight) {
                // const y = $.line

                // if (char === '\n') {
                //   this.shiftDecoTokenLines(y, 1)
                // }

                lines[$.line] =
                  text.slice(0, $.col)
                  + char
                  + indent + (isEnter && isInBrackets ? '\n' + ' '.repeat(beginOfLine) : '')
                  + text.slice($.col)

                $.code = lines.join('\n')
              }
              // this.draw()

              if (isEnter) {
                $.coli = indent.length
                ++$.line
              }
              else {
                $.coli = $.col + char.length
              }
              $.col = $.coli

              if (hasSpaceRight && openers.has(char)) {
                this.handleKey({ key: Open[char] })
                $.coli--
                $.col = $.coli
              }

              $.selectionStart = $.selectionEnd = new Point($.col, $.line)
            }
            break
        }

        if (
          !shiftKey
          && !altKey
          && $.hasSelection
          && (
            !ctrlKey
            || !'zyvxc'.includes(key)
          )
        ) {
          $.selectionStart = $.selectionEnd
        }
      }

      handleOnInput = (e: InputEvent) => {
        const key = e.data!.at(-1)
        if (key) {
          this.handleKey({ key })
          setTimeout(() => {
            $.hiddenTextArea!.value = ''
          }, 50)
        }
        e.preventDefault()
      }

      handleKeyDown = (e: KeyboardEvent) => {
        $.isTyping = true

        this.handleKey(e)

        if (e.ctrlKey || e.metaKey) {
          if ('cvxJr'.includes(e.key)) {
            return
          }
        }

        e.preventDefault()
      }

      handleKeyUp = (e: KeyboardEvent) => {
        $.shiftKey = e.shiftKey
        // switch (e.key) {
        //   case 'Shift':
        //     if (!e.shiftKey) {
        //       $.isSelecting = false
        //     }
        //     break
        // }
      }

      handleCut = historic((e: ClipboardEvent) => {
        this.deleteSelection()
      })

      handlePaste = historic((e: ClipboardEvent) => {
        this.saveHistory()
        const textToPaste = e.clipboardData!.getData("text")
        if ($.hasSelection) this.deleteSelection.sansHistory()
        const index = this.getIndexFromCoords({ x: $.col, y: $.line })
        $.code =
          $.code!.slice(0, index)
          + textToPaste
          + $.code!.slice(index)
        Object.assign($, this.getLineColFromIndex(index + textToPaste.length))
        $.coli = $.col
        e.preventDefault()
      })

      handleWheel = (e: WheelEvent) => {
        $.scrollTop -= e.deltaY * 0.2
        $.scrollLeft -= e.deltaX * 0.2
      }

      getLineColFromPointer = (e: PointerEvent) => {
        const { lineTops, scrollTop, scrollLeft, rect } = $

        const py = e.pageY - scrollTop - rect.y
        const px = e.pageX - scrollLeft - rect.x

        let y = 0
        for (; y < lineTops.length; y++) {
          if (py <= lineTops[y]) break
        }
        --y

        y = clamp(
          0,
          $.lines!.length - 1,
          y
        )

        const x = Math.min(
          $.lines![y].length,
          Math.round((px - 1) / $.charWidth)
        ) // - scrollLeft

        return new Point(x, y)
      }

      getIndexFromCoords = ({ x, y }: { x: number, y: number }) => {
        const lines = $.code!
          .split('\n')
          .slice(0, y)
        return x
          + lines.join('\n').length
          // add the missing \n to the length when line >0
          + (lines.length ? 1 : 0)
      }

      getLineColFromIndex = (index: number): LineCol => {
        const slice = $.code!.slice(0, index)
        const lines = slice.split('\n')
        const line = lines.length - 1
        const col = lines.at(-1)?.length ?? 0
        return { line, col }
      }

      handleLeave = () => {
        $.isHovering =
          $.hoveringMarker =
          $.hoveringSub =
          $.hoveringScrollbarX =
          $.hoveringScrollbarY = false
      }

      handleHover = (e: PointerEvent, kind: 'onPointerDown' | 'onPointerMove') => {
        p.x = e.pageX
        p.y = e.pageY

        $.hoveringScrollbarY = p.withinRect($.scrollbarY)
        $.hoveringScrollbarX = p.withinRect($.scrollbarX)

        if ($.hoveringScrollbarY) {
          if (kind === 'onPointerDown') {
            const sy = p.y
            const top = $.scrollTop
            const co = $.rect.height / $.innerHeight
            const onPointerMove = (e: PointerEvent) => {
              $.scrollTop = top - (e.pageY - sy) / co
            }
            const off = on(window, 'pointermove').capture.stop.prevent(onPointerMove)
            on(window, 'pointerup').once(off)
          }
          return true
        }
        if ($.hoveringScrollbarX) return true

        {
          const p = this.getLineColFromPointer(e)
          if ($.markers) for (const m of $.markers) {
            if (p.y === m.line && p.x >= m.start && p.x <= m.end) {
              if (m[kind]) {
                $.hoveringMarker = m
                $.hoveringSub = false
                m[kind]?.(e)
                return true
              }
            }
          }

          $.hoveringMarker = false

          if ($.sub) for (const s of $.sub) {
            if (p.y === s.line && p.x >= s.start && p.x <= s.end
              && e.pageY - $.scrollTop >= $.lineBottoms[s.line] - s.height - 5
            ) {
              if (s[kind]) {
                $.hoveringMarker = false
                $.hoveringSub = s
                s[kind]?.(e)
                return true
              }
            }
          }

          $.hoveringSub = false
        }
      }

      handleMove = fn(({ hiddenTextArea }) => (e: PointerEvent) => {
        if (this.handleHover(e, 'onPointerMove')) {
          $.cursor = 'default'
          $.isHovering = false
          return
        }

        r.x = e.pageX - 25
        r.y = e.pageY - 25
        r.containSelf($.rect)
        Object.assign(hiddenTextArea.style, r.toStyle())

        // if ($.sub) for (const s of $.sub) {
        //   if (p.y === s.line && p.x >= s.start && p.x <= s.end
        //     && e.pageY - $.scrollTop >= $.lineBottoms[s.line] - s.height
        //   ) {
        //     $.cursor = 'default'
        //     return
        //   }
        // }
        $.cursor = 'text'

        $.isTyping = false
        $.isHovering = true

        const p = this.getLineColFromPointer(e)
        $.hoveringPos = { line: p.y, col: p.x }
      })

      handleMobileMove = (e: PointerEvent) => {

      }


      handleMobileDown = (e: PointerEvent) => {
        e.preventDefault()

        if (this.handleHover(e, 'onPointerDown')) return

        const now = performance.now()

        p.x = e.pageX
        p.y = e.pageY

        const off = on(window, 'pointermove')((e) => {
          const x = e.pageX
          const y = e.pageY

          const dx = x - p.x
          const dy = y - p.y

          if ($.isPanning) {
            $.scrollLeft += dx
            $.scrollTop += dy
            p.x = x
            p.y = y
          }
          else {
            if (Math.abs(dx) + Math.abs(dy) > 1) {
              $.isPanning = true
            }
          }
        })

        on(window, 'pointerup').once((e) => {
          off()
          $.isPanning = false

          if (performance.now() - now < SINGLE_CLICK_MS) {
            this.handleDown(e)
            setTimeout(() => {
              window.dispatchEvent(new PointerEvent('pointerup'))
            }, 20)
          }
        })
      }

      handleDown = (e: PointerEvent) => {
        e.preventDefault()

        if (this.handleHover(e, 'onPointerDown')) return

        const p = this.getLineColFromPointer(e)

        $.hasFocus = true
        $.line = p.y
        $.coli = p.x

        $.selectionStart = $.selectionEnd = p

        const now = performance.now()

        // multi click
        if (now - downTime < 350) {
          downCount++

          // triple click
          if (downCount >= 2) {
            $.selectionStart = new Point(0, $.line)
            $.selectionEnd = new Point($.lines![$.line].length, $.line)
          }
          // double click
          else {
            const words = parseWords(TOKEN, $.lines![$.line])
            for (let i = 0, word: any, next: any; i < words.length; i++) {
              word = words[i]
              next = i < words.length - 1 ? words[i + 1] : { index: Infinity }
              if ($.coli >= word.index && $.coli < next.index) {
                $.selectionStart = new Point(word.index, $.line)
                $.selectionEnd = new Point(word.index + word[0].length, $.line)
              }
            }
          }
        }
        else {
          downCount = 0
        }

        downTime = now

        const off = on(window, 'pointermove')((e) => {
          const np = this.getLineColFromPointer(e)
          $.selectionEnd = np
          $.line = np.y
          $.coli = np.x
        })

        on(window, 'pointerup').once(() => {
          off()
          $.hiddenTextArea!.focus()
        })
      }

      getSelectionSorted = () => {
        let top: Point
        let bottom: Point
        let forward = false

        if ($.selectionStart.y === $.selectionEnd.y) {
          if ($.selectionStart.x < $.selectionEnd.x) {
            top = $.selectionStart
            bottom = $.selectionEnd
            forward = true
          }
          else {
            top = $.selectionEnd
            bottom = $.selectionStart
          }
        }
        else if ($.selectionStart.y < $.selectionEnd.y) {
          top = $.selectionStart
          bottom = $.selectionEnd
          forward = true
        }
        else {
          top = $.selectionEnd
          bottom = $.selectionStart
        }
        return { top, bottom, forward }
      }

      fill = fn(({ tc: c }) => (
        top: Point,
        bottom: Point,
        color: string,
        full = false
      ) => {
        const { lineHeight, lineTops, lineBottoms, lines, subHeights } = $
        if (!lines) return

        for (let y = top.y; y <= bottom.y; y++) {
          c.fillStyle = color
          const x = y === top.y ? top.x * $.charWidth : 0
          const yy = full ? lineTops[y] : lineTops[y + 1] - lineHeight - (subHeights?.get(y) ?? 0) + 1
          const w = (y === top.y ?
            y === bottom.y
              ? (bottom.x - top.x) * $.charWidth + 2
              : (lines[y].length - top.x) * $.charWidth + 5
            : y === bottom.y
              ? bottom.x * $.charWidth + 2
              : (lines[y].length * $.charWidth + 5))
          const h = full ? lineBottoms[y] - lineTops[y] - (y === bottom.y ? 2 : 0) : lineHeight + 0.5
          c.fillRect(
            x,
            yy,
            w,
            h
          )
          if (y === bottom.y) {
            c.fillStyle = skin.$.colors.bg
            c.fillRect(x + w, yy, 2, h)
          }
          if (y === top.y) {
            c.fillStyle = skin.$.colors.bg
            c.fillRect(x - 2, yy, 2, h)
          }
        }
      })

      drawElevation = fn(({ tc: c }) => (ep: LineCol) => {
        // const { scrollTop } = $

        // if (!ep) return

        // const { pr } = screen.$

        // c.save()
        // c.scale(pr, pr)
        // c.translate(1, scrollTop)

        const eligible: { start: Point, end: Point, depth: number }[] = []

        // elevations
        for (let i = 0; i < $.elevations.length; i++) {
          const { start, end } = $.elevations[i]

          if (
            ep.line >= start.y &&
            ep.col >= start.x &&
            ep.line <= end.y &&
            ep.col <= end.x
          ) eligible.push($.elevations[i])
        }

        eligible.sort((a, b) =>
          a.start.y === b.start.y
            ? a.start.x - b.start.x
            : a.start.y - b.start.y
        )

        const [earliest] = eligible

        if (earliest) for (let i = 0; i < $.elevations.length; i++) {
          const { start, end, depth } = $.elevations[i]
          if (
            start.y >= earliest.start.y &&
            start.x >= earliest.start.x &&
            end.y <= earliest.end.y &&
            end.x <= earliest.end.x
          ) this.fill(
            start,
            end,
            luminate(skin.$.colors.bg, 0.015 + (depth ** 1.25) * 0.012), // + (depth ** 1.15) * 0.018),
            true
          )
        }

        const latest = eligible.at(-1)

        if (latest) {
          // const hasElevation = $.elevations.some((x) => open.line === x.start.y && open.col === x.start.x)

          this.fill(
            latest.start,
            latest.end,
            luminate(skin.$.colors.bg, 0.18) + '28',
            true
          )
        }

        // c.restore()
      })

      drawText = fn(({ rect, tc: c }) => () => {
        const { fontSize, lineHeight, lineTops, lineBottoms, scrollTop, scrollLeft, tokens, line, col, lines, subHeights } = $
        if (!tokens) return

        c.clearRect(0, 0, rect.width, rect.height)

        c.save()

        c.translate(1 + scrollLeft, scrollTop)

        c.font = `100 ${$.fontSize}px ${skin.$.fonts.mono}`
        c.textBaseline = 'bottom'

        const dims = c.measureText('M')

        $.charWidth = dims.width

        const viewTop = -scrollTop
        const viewBottom = viewTop + rect.height + lineHeight


        // active line

        // if (!$.brackets) {
        c.fillStyle = luminate(skin.$.colors.bg, 0.015)
        c.fillRect(
          -scrollLeft,
          lineTops[line] + 1,
          rect.width,
          lineTops[line + 1] - lineTops[line] - 3
        )
        // }


        if ($.elevationPoint) {
          this.drawElevation($.elevationPoint)
        }

        this.drawElevation({ line, col })

        // brackets background

        // if ($.brackets) {

        //   const [open, close] = $.brackets

        //   const eligible: { start: Point, end: Point, depth: number }[] = []

        //   // elevations
        //   for (let i = 0; i < $.elevations.length; i++) {

        //     const { start, end } = $.elevations[i]

        //     if (
        //       open.line >= start.y &&
        //       open.col >= start.x &&
        //       close.line <= end.y &&
        //       close.col <= end.x
        //     ) eligible.push($.elevations[i])
        //   }

        //   eligible.sort((a, b) =>
        //     a.start.y === b.start.y
        //       ? a.start.x - b.start.x
        //       : a.start.y - b.start.y
        //   )

        //   const [earliest] = eligible

        //   if (earliest) for (let i = 0; i < $.elevations.length; i++) {
        //     const { start, end, depth } = $.elevations[i]
        //     if (
        //       start.y >= earliest.start.y &&
        //       start.x >= earliest.start.x &&
        //       end.y <= earliest.end.y &&
        //       end.x <= earliest.end.x
        //     ) this.fill(
        //       start,
        //       end,
        //       luminate(skin.$.colors.bg, 0.015 + (depth ** 1.25) * 0.012), // + (depth ** 1.15) * 0.018),
        //       true
        //     )
        //   }

        //   const hasElevation = $.elevations.some((x) => open.line === x.start.y && open.col === x.start.x)

        //   this.fill(
        //     new Point(open.col, open.line),
        //     new Point(close.col + 1, close.line),
        //     luminate(skin.$.colors.bg, 0.18) + '28',
        //     hasElevation
        //   )
        // }

        // selection

        if ($.hasSelection && lines && $.selectionStart && $.selectionEnd) {
          const { top, bottom } = this.getSelectionSorted()
          this.fill(top, bottom, '#477')
        }

        // brackets

        if ($.brackets && lines) {
          c.strokeStyle = '#f2a'
          c.lineWidth = 1

          const [open, close] = $.brackets
          // if (lines[open.line][open.col] === '[') {

          // }
          const hasElevation = false //lines![open.line][open.col] === '[' && $.elevations.some((x) => open.line === x.start.y && open.col === x.start.x)

          const x1 = open.col * $.charWidth + 1
          const y1 = hasElevation ? lineTops[open.line] : lineTops[open.line + 1] - (subHeights?.get(open.line) ?? 0) - lineHeight + 1
          const x2 = close.col * $.charWidth + (hasElevation ? $.charWidth - 1 : 0) + 1
          const y2 = hasElevation ? lineTops[close.line] : lineTops[close.line + 1] - (subHeights?.get(close.line) ?? 0) - lineHeight + 1

          const h1 = hasElevation ? lineBottoms[open.line] - y1 - (open.line === close.line ? 1 : open.line < close.line ? 0 : 2) : lineHeight
          const h2 = hasElevation ? lineBottoms[close.line] - y2 - (open.line === close.line ? 1 : 2) : lineHeight

          const w = hasElevation ? 3 : $.charWidth

          c.strokeRect(
            x1,
            y1,
            w, //$.charWidth + 1,
            h1 // lineHeight
          )
          c.strokeRect(
            x2,
            y2,
            w, //$.charWidth + 1,
            h2, //lineHeight
          )
        }

        // text
        c.lineWidth = fontSize / 100

        for (let i = 0, t: SyntaxToken, x: number, y: number; i < tokens.length; i++) {
          t = tokens[i]
          if (!t.type || !t.text) continue

          y = lineTops[t.line + 1] - (subHeights?.get(t.line) ?? 0) // * lineHeight
          // console.log(y, t.text)
          if (y > viewTop && y < viewBottom) {
            x = t.col * $.charWidth + 1
            c.fillStyle =
              // @ts-ignore
              c.strokeStyle = skin.$.colors[colors[t.text] ?? colors[t.type]] ?? '#fff'

            //   if (t.text === '[' || t.text === ']') {
            //   const top = lineTops[t.line] + 2.25
            //   const bottom = lineBottoms[t.line] - 0.5

            //   c.save()
            //   c.lineWidth = 1.5
            //   c.translate(2, -0.5)
            //   if (t.text === '[') {
            //     x += 1.5
            //     c.beginPath()
            //     c.moveTo(x + 5, top)
            //     c.lineTo(x, top)
            //     c.lineTo(x, bottom)
            //     c.lineTo(x + 5, bottom)
            //   }
            //   else {
            //     x += 2
            //     c.beginPath()
            //     c.moveTo(x - 5, top)
            //     c.lineTo(x, top)
            //     c.lineTo(x, bottom)
            //     c.lineTo(x - 5, bottom)
            //   }
            //   c.stroke()
            //   c.restore()
            //   // c.font = `100 ${lineBottoms[t.line] - lineTops[t.line]}px ${skin.$.fonts.mono}`
            // }
            // else {
            c.fillText(t.text, x, y)
            c.strokeText(t.text, x, y)
            // }

          }
        }

        c.restore()
      })

      drawDeco = fn(({ c, textCanvas }) => () => {
        const { rect, lineHeight, lineTops, tokens, deco, scrollTop, scrollLeft } = $
        if (!tokens || !deco) return

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)
        c.translate(scrollLeft, scrollTop)

        const viewTop = -scrollTop
        const viewBottom = viewTop + rect.height + lineHeight

        // deco
        for (let i = 0, d: Deco,
          x: number, y: number, w: number, h: number,
          x2: number, y2: number, w2: number, h2: number
          ; i < deco.length; i++) {
          d = deco[i]
          d.update?.()

          h = d.height - 2
          // if (d.preserveAspectRatio) {
          //   if (w === h) { }
          //   else if (w < h) {
          //     h *= w / h
          //   }
          //   else {
          //     w *= h / w
          //   }
          // }
          // console.log(d.line, lineTops[d.line])
          y = lineTops[d.line] + 2

          if (y + h < viewTop || y > viewBottom) {
            d.isVisible = false
            continue
          }
          d.isVisible = true

          x = d.start * $.charWidth + 3.35
          w = (d.end - d.start) * $.charWidth

          // console.log(y + scrollTop)

          // c.clearRect(x, y, w, h)
          // c.drawImage(textCanvas,
          //   (x + scrollLeft) * pr + pr, (y + scrollTop) * pr, w * pr, h * pr,
          //   x, y, w, h,
          // )

          x2 = x - 1
          y2 = y - 1
          w2 = w + 2
          h2 = h + 2

          c.clearRect(x2, y2, w2, h2)
          c.drawImage(textCanvas,
            (x2 + scrollLeft) * pr, (y2 + scrollTop) * pr, w2 * pr, h2 * pr,
            x2, y2, w2, h2,
          )

          if (d.canvas) {
            c.drawImage(d.canvas, x, y, w, h)
          }
          else if (d.draw) {
            d.draw(c, x, y, w, h, d)
            // d.draw(c, x + 1, y + 1, w - 1.5, h - 2.5, d)
          }
        }
        c.restore()

        this.drawSub()
        this.drawMarkers()
        this.drawCaret()
        this.drawCursors()
        this.drawScrollbars()
      })

      drawScrollbars = fn(({ c, textCanvas }) => () => {
        const { rect, innerHeight, innerWidth, scrollTop, scrollLeft, scrollbarX: sX, scrollbarY: sY } = $

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)

        {
          const co = rect.width / innerWidth
          const x = -scrollLeft * co
          const w = rect.width * co

          sX.x = x
          sX.y = rect.height - 7
          sX.w = w
          sX.h = 7

          c.fillStyle = luminate(skin.$.colors.bg, $.hoveringScrollbarX ? 0.2 : 0.1)
          c.fillRect(sX.x, sX.y, sX.w, sX.h)
        }

        {
          const co = rect.height / innerHeight
          const y = -scrollTop * co
          const h = rect.height * co

          sY.x = rect.width - 20
          sY.y = y
          sY.width = 20
          sY.height = h

          c.fillStyle = luminate(skin.$.colors.bg, $.hoveringScrollbarY ? 0.2 : 0.1)
          c.fillRect(sY.x, sY.y, sY.w, sY.h)
        }

        c.restore()
      })

      drawSub = fn(({ c, textCanvas }) => () => {
        const { lineBottoms, tokens, sub, scrollTop, scrollLeft } = $
        if (!tokens || !sub) return

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)
        c.translate(scrollLeft + 1, scrollTop)

        // sub
        for (let i = 0, s: Sub, x: number, y: number, w: number, h: number; i < sub.length; i++) {
          s = sub[i]
          w = (s.end - s.start) * $.charWidth
          h = s.height
          x = s.start * $.charWidth + 1
          y = lineBottoms[s.line] - s.height - 4

          c.clearRect(x, y, w, h)
          c.drawImage(textCanvas,
            (x + scrollLeft) * pr, (y + scrollTop) * pr, w * pr, h * pr,
            x, y, w, h,
          )
          s.draw(c, x, y, w, h, s)
        }
        c.restore()
      })

      drawMarkers = fn(({ c, textCanvas }) => () => {
        const { lineTops, lineHeight, tokens, markers, scrollTop, scrollLeft, subHeights } = $
        if (!tokens || !markers) return

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)
        c.translate(scrollLeft, scrollTop)

        // markers
        for (let i = 0, m: Marker,
          x: number, y: number, w: number, h: number,
          x2: number, y2: number, w2: number, h2: number
          ; i < markers.length; i++) {
          m = markers[i]
          w = (m.end - m.start) * $.charWidth + 4
          h = lineHeight // d.height
          x = m.start * $.charWidth
          y = lineTops[m.line + 1] - (subHeights?.get(m.line) ?? 0) - lineHeight + 1

          x2 = x - 1
          y2 = y - 1
          w2 = w + 2
          h2 = h + 2

          c.clearRect(x2, y2, w2, h2)
          c.drawImage(textCanvas,
            (x2 + scrollLeft) * pr, (y2 + scrollTop) * pr, w2 * pr, h2 * pr,
            x2, y2, w2, h2,
          )

          m.draw(c, x, y, w, h, m)
        }
        c.restore()
      })

      drawIndicator = fn(({ rect, c }) => (
        x: number,
        y: number,
        w: number,
        h: number,
        color1: string,
        color2: string,
      ) => {
        c.fillStyle = color1

        c.beginPath()
        c.moveTo(x, y + 3)
        c.lineTo(x - w, y - w)
        c.lineTo(x + w, y - w)
        c.lineTo(x + 1, y + 3)
        c.fill()
        c.beginPath()
        c.moveTo(x + 1, y + 3)
        c.lineTo(x + 1, y + h)
        c.lineTo(x, y + h)
        c.lineTo(x, y + 3)
        c.fillStyle = color2
        c.fill()
      })

      drawCaret = fn(({ rect, c }) => () => {
        const { hasFocus, lineTops, lineHeight, lineBottoms, subHeights, scrollTop, scrollLeft, charWidth, line, col } = $
        if (!$.hoveringPos) return

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)
        c.translate(scrollLeft + 1, scrollTop)

        if (!$.isTyping && $.isHovering) {
          const pos = $.hoveringPos
          const y = lineTops[pos.line] + 1
          const h = lineBottoms[pos.line] - y - (subHeights?.get(pos.line) ?? 0) + 1
          const x = pos.col * charWidth
          const w = 7

          this.drawIndicator(x, y, w, h,
            '#455', '#233')
        }

        const pos = { line, col }
        const y = lineTops[pos.line] + 1
        const h = lineBottoms[pos.line] - y - (subHeights?.get(pos.line) ?? 0) + 1
        const x = pos.col * charWidth
        const w = 7

        this.drawIndicator(x, y, w, h,
          hasFocus ? '#49f' : '#455', hasFocus ? '#25a' : '#233')

        // caret

        if (hasFocus) {
          c.fillStyle = '#fff'
          c.fillRect(
            col * $.charWidth,
            lineBottoms[line] - lineHeight - (subHeights?.get(line) ?? 0) + 1,
            1,
            lineHeight
          )
        }

        c.restore()
      })

      drawCursors = fn(({ rect, c }) => () => {
        const { lineTops, lineBottoms, subHeights, scrollTop, scrollLeft, charWidth } = $

        const { pr } = screen.$

        c.save()
        c.scale(pr, pr)
        c.translate(scrollLeft + 1, scrollTop)
        $.cursors.forEach((p) => {
          const pos = { line: p.y, col: p.x }
          const y = lineTops[pos.line] + 1
          const h = lineBottoms[pos.line] - y - (subHeights?.get(pos.line) ?? 0) + 1
          const x = pos.col * charWidth
          const w = 7

          this.drawIndicator(x, y, w, h,
            p.color!, p.color!)
        })

        c.restore()
      })

      draw = fn(({ rect, c }) => () => {
        this.drawText()
        this.redraw()
      })

      redraw = fn(({ rect, c }) => () => {
        c.save()
        c.scale(screen.$.pr, screen.$.pr)
        c.clearRect(0, 0, rect.width, rect.height)
        c.restore()
        c.drawImage($.textCanvas, 0, 0)
        this.drawDeco()
      })

      resize = fn(({ host }) => async () => {
        const rect = host.getBoundingClientRect()
        if (!rect.width || !rect.height) return
        $.rect = new Rect(rect)
      })
    })
  },
  function effects({ $, fx, deps, refs }) {
    fx(() => skin.fx(({ fonts, colors }) => {
      $.css = /*css*/`
      & {
        font-family: ${fonts.mono};
        overflow: hidden;
        cursor: text;
        touch-action: none;
      }

      * { touch-action: none; }

      textarea {
        all: unset;
        opacity: 0;
        position: fixed;
        pointer-events: none;
        overflow: hidden;
        z-index: 999999;
      }
      `
      $.draw()
    }))

    fx(({ host }) =>
      observe.resize.initial(host, $.resize)
    )

    fx(({ host, cursor }) => {
      host.style.cursor = cursor
    })

    skin.fx(({ fontFaces }) => {
      $.fontsReady = true
    })

    // fx(({ canvas }) => {
    //   document.fonts.ready.then(() => {
    //     $.fontsReady = true
    //   })
    // })

    fx(({ value }) =>
      effect({ value }, ({ value }) => {
        $.code = value
      })
    )

    fx(({ value, code }) => {
      value.value = code
    })

    fx(() => screen.fx(({ pr }) =>
      fx(({ canvas, rect, c, textCanvas, tc, fontsReady }) => {
        canvas.width = rect.width * pr
        canvas.height = rect.height * pr
        canvas.style.width = rect.width + 'px'
        canvas.style.height = rect.height + 'px'

        textCanvas.width = rect.width * pr
        textCanvas.height = rect.height * pr
        textCanvas.style.width = rect.width + 'px'
        textCanvas.style.height = rect.height + 'px'

        // c.scale(pr, pr)
        tc.scale(pr, pr)
        if (fontsReady) $.draw()
      })
    ))

    fx(({ canvas }) => {
      $.c = canvas.getContext('2d')!
    })

    fx(({ fontSize }) => {
      $.lineHeight = Math.round(fontSize * 1.35)
    })

    fx(({ code }) => {
      $.lines = code.split('\n')
      $.saveHistoryDebounced()
    })

    fx(({ deco }) => {
      // console.log('RECEIVE DECO', deco)
      $.decoHeights = $.getDecoHeights()

      // const decoMap = new Map<number, Deco[]>()
      // const decoHeights = new Map<number, number>()

      // for (const d of deco) {
      //   if (decoMap.has(d.line)) {
      //     decoMap.get(d.line)!.push(d)
      //   }
      //   else {
      //     decoMap.set(d.line, [d])
      //   }

      //   decoHeights.set(
      //     d.line,
      //     Math.max(
      //       decoHeights.get(d.line) ?? 0,
      //       d.height
      //     )
      //   )
      // }

      // $.decoMap = decoMap
      // $.decoHeights = decoHeights
    })

    fx(({ sub }) => {
      const subMap = new Map<number, Sub[]>()
      const subHeights = new Map<number, number>()

      for (const s of sub) {
        if (subMap.has(s.line)) {
          subMap.get(s.line)!.push(s)
        }
        else {
          subMap.set(s.line, [s])
        }

        subHeights.set(
          s.line,
          Math.max(
            subHeights.get(s.line) ?? 0,
            s.height + 4
          )
        )
      }

      $.subMap = subMap
      $.subHeights = subHeights
    })

    fx(({ lines, line }) => {
      for (let i = 0; i < lines.length; i++) {
        if (i === line) continue
        lines[i] = lines[i].trimEnd()
      }
      // $.code = lines.join('\n')
    })

    fx(({ lines }) => {
      let lineLongest = 0
      for (let i = 0, lineLength = 0; i < lines.length; i++) {
        lineLength = lines[i].length
        if (lineLength > lineLongest) lineLongest = lineLength
      }
      $.lineLongest = lineLongest
    })

    fx(({ lines, lineHeight, decoHeights, subHeights }) => {
      $.updateLineTops()
    })

    fx(({ lineBottoms }) => {
      $.innerHeight = lineBottoms.at(-1) ?? 0
    })

    fx(({ lineLongest, charWidth }) => {
      $.innerWidth = lineLongest * charWidth
    })

    fx(({ code, selectionStart: _0, selectionEnd: _1 }) => {
      const { top, bottom } = $.getSelectionSorted()
      const a = $.getIndexFromCoords(top)
      const b = $.getIndexFromCoords(bottom)
      $.selectionText = code.slice(a, b)
    })

    fx.raf(({ selectionText, hiddenTextArea }) => {
      hiddenTextArea.value = selectionText
      hiddenTextArea.select()
    })

    fx(({ lines, line, coli }) => {
      $.col = Math.min(coli, lines[line]?.length)
      // $.line = Math.min(line, lines.length)
    })

    fx(({ selectionStart, selectionEnd }) => {
      $.hasSelection = !selectionStart.equals(selectionEnd)
    })

    fx(({ brackets, isTyping, hoveringPos, line, col }) => {
      if (!isTyping) {
        $.elevationPoint = hoveringPos
      }
      else if (brackets) {
        $.elevationPoint = brackets[0]
      }
      else {
        $.elevationPoint = false
      }
    })

    fx(({ tokens, elevated }) => {
      const stack: [SyntaxToken, boolean][] = []
      const elevations: { start: Point, end: Point, depth: number }[] = []

      for (const t of tokens) {
        if (openers.has(t.text)) {
          stack.push([t, t.text === '{' ? true : elevated.some((x) => x.line === t.line && x.col === t.col)])
        }
        else if (closers.has(t.text)) {
          const item = stack.pop()
          if (item) {
            const [start, is] = item
            if (is) {
              const end = t
              const p1 = new Point(start.col, start.line)
              const p2 = new Point(end.col + 1, end.line)
              elevations.push({ start: p1, end: p2, depth: stack.length })
            }
          }
        }
      }
      $.elevations = elevations.sort((a, b) => a.depth - b.depth)
    })

    fx(({ hoveringMarker, hoveringSub }) => () => {
      if (hoveringMarker) {
        hoveringMarker.onPointerLeave?.()
      }
      else if (hoveringSub) {
        hoveringSub.onPointerLeave?.()
      }
    })

    fx.task(({
      fontsReady,
      tokens: _to,
      line: _li,
      col: _co,
      selectionStart: _ss,
      selectionEnd: _se,
      scrollTop: _st,
      scrollLeft: _sl,
      lines: _lns,
      hasFocus: _hf,
      decoHeights: _de,
      lineTops: _lh,
      isHovering: _ih,
      hoveringPos: _ho,
      elevationPoint: _ep,
      cursors: _cu,
      hoveringScrollbarY,
      hoveringScrollbarX,
    }) => {
      if (fontsReady) $.draw()
    })

    fx(({ code, line, col }) => {
      const index = $.getIndexFromCoords({ y: line, x: col })
      const brackets = findMatchingBrackets(code, index)
      if (!brackets) {
        $.brackets = false
        return
      }

      $.brackets = [
        $.getLineColFromIndex(brackets[0]),
        $.getLineColFromIndex(brackets[1]),
      ]
    })

    fx.task(({ line, col, shiftKey }) => {
      if (shiftKey) {
        $.selectionEnd = new Point(col, line)
      }
    })

    fx(({ rect, innerHeight, scrollTop, lineBottoms }) => {
      $.scrollTop = clamp(
        -innerHeight
        + Math.min(
          lineBottoms.at(-1) ?? 0,
          rect.height
        ),
        0,
        scrollTop
      )
    })

    fx(({ rect, innerWidth, scrollLeft, charWidth }) => {
      $.scrollLeft = clamp(
        -innerWidth + rect.width - charWidth,
        0,
        scrollLeft
      )
    })

    fx(({ rect, line, lineTops, lineBottoms, lineHeight }) => {
      const { scrollTop /*, scrollLeft*/ } = $

      const viewTop = -scrollTop
      const viewBottom = viewTop + rect.height + lineHeight

      let y = lineTops[line]

      let dy

      dy = viewTop - y
      if (dy > 0) {
        $.scrollTop += dy
      }
      else {
        y = lineBottoms[line] + lineHeight
        dy = y - viewBottom
        if (dy > 0) {
          $.scrollTop -= dy
        }
      }
    })

    fx(({ rect, col, charWidth, innerWidth }) => {
      const { scrollLeft } = $

      const viewLeft = -scrollLeft
      const viewRight = viewLeft + rect.width

      let x = col * charWidth

      let dx

      dx = viewLeft - (x - charWidth * 10)
      if (dx > 0) {
        $.scrollLeft += dx
      }
      else {
        x += charWidth * 10
        dx = x - viewRight
        if (dx > 0) {
          $.scrollLeft -= dx
        }
      }
      // let y = lineTops[line]

      // let dy

      // dy = viewTop - y
      // if (dy > 0) {
      //   $.scrollTop += dy
      // }
      // else {
      //   y = lineBottoms[line] + lineHeight
      //   dy = y - viewBottom
      //   if (dy > 0) {
      //     $.scrollTop -= dy
      //   }
      // }
    })

    // fx(({ code }) => {
    //   const tokens = lex(code)
    //   // try {
    //   //   const def = make(parse(tokens).program)
    //   // } catch {}
    //   $.tokens = tokens
    // })

    // fx(({ host }) =>
    //   on(host, 'dblclick').prevent.stop()
    // )
    // fx(() => chain(
    //   on(window, 'pointerleave')($.handleLeave),
    //   // on(window, 'keydown')($.handleKeyUp),
    // ))

    fx(() => {
      $.view = <>
        <textarea
          ref={refs.hiddenTextArea}
          spellcheck='false'
          autocorrect='off'
          oninput={IS_MOBILE ? $.handleOnInput : () => { }}
          // onchange={$.handleOnInput}
          onkeydown={$.handleKeyDown}
          onkeyup={$.handleKeyUp}
          oncut={$.handleCut}
          onpaste={$.handlePaste}
          onblur={$.handleBlur}
          onfocus={$.handleFocus}
        />
        <canvas
          ref={refs.canvas}
          onwheel={$.handleWheel}
          onpointerdown={IS_MOBILE ? $.handleMobileDown : $.handleDown}
          onpointermove={IS_MOBILE ? $.handleMobileMove : $.handleMove}
          onpointerleave={$.handleLeave}
        />
      </>
    })
  }
)))

export function test() {
  async function rest() {
    await Promise.resolve()
    await Promise.resolve()
    await Promise.resolve()
  }
  describe('Editor', () => {
    it('ctrl backspace edge', async () => {
      const el = document.createElement('div')
      const value = dep('Hello there\nWorld')

      render(<Editor
        name='foo'
        rect={new Rect(0, 0, 300, 300)}
        value={value}
      />, el)

      await rest()

      const editor = el.firstChild as unknown as Editor
      editor.$.coli = 'Hello there'.length

      await rest()
      editor.$.handleKey({ key: 'Backspace', ctrlKey: true })
      await rest()

      expect(value.value).toBe('Hello \nWorld')
    })

    it('shift home backspace', async () => {
      const el = document.createElement('div')
      const value = dep('Hello there\nWorld')

      render(<Editor
        name='foo'
        rect={new Rect(0, 0, 300, 300)}
        value={value}
      />, el)

      await rest()

      const editor = el.firstChild as unknown as Editor
      editor.$.coli = 'Hello there'.length

      await rest()
      editor.$.handleKey({ key: 'Shift' })
      await rest()
      editor.$.handleKey({ key: 'Home', shiftKey: true })
      await rest()
      editor.$.handleKey({ key: 'Backspace' })
      await rest()

      expect(value.value).toBe('\nWorld')
    })

    it('shift home backspace line > 0', async () => {
      const el = document.createElement('div')
      const value = dep('Hello there\nWorld')

      render(<Editor
        name='foo'
        rect={new Rect(0, 0, 300, 300)}
        value={value}
      />, el)

      await rest()

      const editor = el.firstChild as unknown as Editor
      editor.$.line = 1
      editor.$.coli = 'World'.length

      await rest()
      editor.$.handleKey({ key: 'Shift' })
      await rest()
      editor.$.handleKey({ key: 'Home', shiftKey: true })
      await rest()
      editor.$.handleKey({ key: 'Backspace' })
      await rest()

      expect(value.value).toBe('Hello there\n')
    })

    it('ctrl backspace', async () => {
      const el = document.createElement('div')
      const value = dep('Hello there\nWorld')

      render(<Editor
        name='foo'
        rect={new Rect(0, 0, 300, 300)}
        value={value}
      />, el)

      await rest()

      const editor = el.firstChild as unknown as Editor
      editor.$.line = 0
      editor.$.coli = 'Hello there'.length

      await rest()
      editor.$.handleKey({ key: 'Backspace', ctrlKey: true })
      await rest()

      expect(value.value).toBe('Hello \nWorld')
    })
  })
}
